import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styles from "../styles/layout/initialQuestionare.module.css";
import { Box } from "@mui/material";
import NirantaraLogo from "../assets/images/vajra_logo.svg";
import { getConsultantLogoUrl } from "../utils/consultantLogo";
import { get } from "../utils/api";

const initialSidebarList = [
  {
    heading: "Company Information",
    path: "/company-information",
    subItems: [],
    checked: false,
    boxColor: "#1C486B",
    key: "company_information",
  },
  {
    heading: "Sustainability",
    path: "/sustainability",
    subItems: [],
    checked: false,
    boxColor: "#1C486B",
    key: "sustainability",
  },
  {
    heading: "Challenges & Concerns",
    path: "/challenges-concerns",
    subItems: [],
    checked: false,
    boxColor: "#1C486B",
    key: "challenges_concerns",
  },
  {
    heading: "Future Plans",
    path: "/future-plans",
    subItems: [],
    checked: false,
    boxColor: "#1C486B",
    key: "future_plans",
  },
  {
    heading: "Material Topics",
    path: "/material-topics",
    subItems: [],
    checked: false,
    boxColor: "#1C486B",
    key: "material_topics",
  },
  {
    heading: "ESG Baseline",
    path: "/esg-baseline",
    subItems: [],
    checked: false,
    boxColor: "#1C486B",
    key: "esg_baseline",
  },
  {
    heading: "Environment",
    path: "/environment",
    checked: false,
    boxColor: "#488257",
    subItems: [
      {
        heading: "Energy Management",
        path: "/environment/energy-management",
        checked: false,
        key: "energy_management",
      },
      {
        heading: "Greenhouse Gas Emissions",
        path: "/environment/ghg-emissions",
        checked: false,
        key: "ghg_emissions",
      },
      {
        heading: "Water Management",
        path: "/environment/water-management",
        checked: false,
        key: "water_management",
      },
      {
        heading: "Waste Management",
        path: "/environment/waste-management",
        checked: false,
        key: "waste_management",
      },
      {
        heading: "Biodiversity & Land Use",
        path: "/environment/biodiversity",
        checked: false,
        key: "biodiversity",
      },
      {
        heading: "Pollution & Environmental Compliance",
        path: "/environment/pollution",
        checked: false,
        key: "pollution",
      },
      {
        heading: "Sustainable Sourcing & Supply Chain",
        path: "/environment/sustainable-sourcing",
        checked: false,
        key: "sustainable_sourcing",
      },
    ],
  },
  {
    heading: "Social",
    path: "/social",
    checked: false,
    boxColor: "#EB7E0E",
    subItems: [
      {
        heading: "Employee Well Being & Development",
        path: "/social/employee-development",
        checked: false,
        key: "employee_development",
      },
      {
        heading: "Diversity & Inclusion",
        path: "/social/diversity",
        checked: false,
        key: "diversity",
      },
      {
        heading: "Community Engagement",
        path: "/social/community",
        checked: false,
        key: "community",
      },
      {
        heading: "Human Rights",
        path: "/social/human-right",
        checked: false,
        key: "human_right",
      },
      {
        heading: "Customer Relations",
        path: "/social/customer-relation",
        checked: false,
        key: "customer_relation",
      },
      {
        heading: "Supply Chain Management",
        path: "/social/supply-chain",
        checked: false,
        key: "supply_chain",
      },
    ],
  },
  {
    heading: "Governance",
    path: "/governance",
    checked: false,
    boxColor: "#1C486B",
    subItems: [
      {
        heading: "Board Composition & Effectiveness",
        path: "/governance/board-composition",
        checked: false,
        key: "board_composition",
      },
      {
        heading: "Executive Compensation",
        path: "/governance/executive-compensation",
        checked: false,
        key: "executive_compensation",
      },
      {
        heading: "Ethics & Compliance",
        path: "/governance/ethics",
        checked: false,
        key: "ethics",
      },
      {
        heading: "Risk Management",
        path: "/governance/risk-management",
        checked: false,
        key: "risk_management",
      },
      {
        heading: "Stakeholder Engagement",
        path: "/governance/stakeholder",
        checked: false,
        key: "stakeholder",
      },
      {
        heading: "Cybersecurity & Data Privacy",
        path: "/governance/cybersecurity",
        checked: false,
        key: "cybersecurity",
      },
    ],
  },
  { heading: "Preview", path: "/preview", subItems: [], checked: false },
];

const InitialQuestionareSidebar = () => {
  const sidebarRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const [expandedSections, setExpandedSections] = useState({});
  const [checkedState, setCheckedState] = useState(() =>
    initialSidebarList.reduce((acc, item) => {
      acc[item.path] = item.checked || false;
      item.subItems?.forEach((sub) => {
        acc[sub.path] = sub.checked || false;
      });
      return acc;
    }, {})
  );

  const fetchSidebarData = async () => {
    await get("/api/questionnaire/status")
      .then((response) => {
        const status = response?.data?.data || {};
        const newCheckedState = {};
        initialSidebarList.forEach((item) => {
          if (item.subItems && item.subItems.length > 0) {
            // Check if all subItems are true
            const allSubItemsChecked = item.subItems.every(
              (sub) => sub.key && status[sub.key]
            );
            newCheckedState[item.path] = allSubItemsChecked;
            // Set subItems as before
            item.subItems.forEach((sub) => {
              if (sub.key) {
                newCheckedState[sub.path] = !!status[sub.key];
              } else {
                newCheckedState[sub.path] = false;
              }
            });
          } else {
            // No subItems, use the item's own key
            if (item.key) {
              newCheckedState[item.path] = !!status[item.key];
            } else {
              newCheckedState[item.path] = false;
            }
          }
        });
        setCheckedState(newCheckedState);
      })
      .catch((error) => {
        console.error("Error fetching sidebar data:", error);
      });
  };

  // Fetch sidebar data on mount
  useEffect(() => {
    fetchSidebarData();
  }, []);

  // Auto-expand parent sections when sub-item is active
  useEffect(() => {
    const currentPath = location.pathname;

    // Find if current path is a sub-item and expand its parent
    initialSidebarList.forEach((item) => {
      if (item.subItems && item.subItems.length > 0) {
        const hasActiveSubItem = item.subItems.some(
          (sub) => sub.path === currentPath
        );
        if (hasActiveSubItem) {
          setExpandedSections((prev) => ({
            ...prev,
            [item.path]: true,
          }));
        }
      }
    });
  }, [location.pathname]);

  // Preserve scroll position
  useEffect(() => {
    const sidebarEl = sidebarRef.current;
    if (!sidebarEl) return;

    const handleScroll = () => {
      localStorage.setItem("initialSidebarScrollTop", sidebarEl.scrollTop);
    };

    sidebarEl.addEventListener("scroll", handleScroll);
    return () => sidebarEl.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const sidebarEl = sidebarRef.current;
    const savedScrollTop = localStorage.getItem("initialSidebarScrollTop");
    if (sidebarEl && savedScrollTop) {
      sidebarEl.scrollTop = parseInt(savedScrollTop, 10);
    }
  }, []);

  // Helper function to check if a parent section should be highlighted
  const isParentActive = (item) => {
    if (!item.subItems || item.subItems.length === 0) return false;
    return item.subItems.some((sub) => sub.path === location.pathname);
  };

  const isCompanyInformationComplete =
    checkedState["/company-information"] === true;

  const isSidebarItemDisabled = (path) =>
    path !== "/company-information" && !isCompanyInformationComplete;

  const toggleSection = (path, hasSubItems) => {
    if (isSidebarItemDisabled(path)) return;

    if (hasSubItems) {
      setExpandedSections((prev) => ({
        ...prev,
        [path]: !prev[path],
      }));
    } else {
      navigate(path);
    }
  };

  const toggleChecked = (e, path) => {
    e.stopPropagation(); // Prevent click from bubbling to parent
    setCheckedState((prev) => ({
      ...prev,
      [path]: !prev[path],
    }));
  };

  const renderSidebarItems = () =>
    initialSidebarList.map((item, idx) => {
      const isActive = location.pathname === item.path;
      const isParentHighlighted = isParentActive(item);
      return (
        <React.Fragment key={item.path}>
          <div
            className={styles.sidebarItem}
            onClick={() => toggleSection(item.path, item.subItems?.length > 0)}
          >
            <div
              className={
                checkedState[item.path]
                  ? styles.sidebarDotChecked
                  : isActive || isParentHighlighted
                  ? styles.sidebarDotActive
                  : styles.sidebarDot
              }
            >
              {checkedState[item.path] && (
                <span className={styles.checkmark}>✓</span>
              )}
            </div>
            <p
              className={styles.sidebarText}
              style={
                isActive || isParentHighlighted
                  ? {
                      backgroundColor: item.boxColor || "#1C486B",
                      color: "#fff",
                      borderRadius: "4px",
                      padding: "10px 6px",
                      width: "100%",
                      wordWrap: "break-word",
                      whiteSpace: "normal",
                      overflowWrap: "break-word",
                      lineHeight: "1.2",
                    }
                  : {}
              }
            >
              {item.heading}
            </p>
          </div>

          {expandedSections[item.path] &&
            item.subItems.map((sub, sIdx, subArray) => {
              const isSubActive = location.pathname === sub.path;
              return (
                <React.Fragment key={sub.path}>
                  {sIdx === 0 && (
                    <div className={styles.sidebarConnector}></div>
                  )}

                  <div
                    className={styles.sidebarSubItem}
                    style={{ display: "flex", alignItems: "center" }}
                    onClick={() => {
                      if (!isCompanyInformationComplete) return;
                      navigate(sub.path);
                    }}
                  >
                    <div
                      className={
                        checkedState[sub.path]
                          ? styles.sidebarDotChecked
                          : isSubActive
                          ? styles.sidebarDotActive
                          : styles.sidebarDot
                      }
                    >
                      {checkedState[sub.path] && (
                        <span className={styles.checkmark}>✓</span>
                      )}
                    </div>
                    <p
                      className={styles.sidebarText}
                      style={
                        isSubActive
                          ? {
                              backgroundColor:
                                sub.boxColor || item.boxColor || "#1C486B",
                              color: "#fff",
                              borderRadius: "4px",
                              padding: "8px 6px",
                              width: "100%",
                              wordWrap: "break-word",
                              whiteSpace: "normal",
                              overflowWrap: "break-word",
                              lineHeight: "1.2",
                            }
                          : {}
                      }
                    >
                      {sub.heading}
                    </p>
                  </div>

                  {sIdx !== subArray.length - 1 && (
                    <div className={styles.sidebarConnector}></div>
                  )}
                </React.Fragment>
              );
            })}

          {idx !== initialSidebarList.length - 1 && (
            <div className={styles.sidebarConnector}></div>
          )}
        </React.Fragment>
      );
    });

  return (
    <Box className={styles.sidebarContainer}>
      <Box
        display="flex"
        justifyContent="center"
        mb={1}
        // style={{ borderBottom: "1px solid #ccc" }}
      >
        <img
          src={getConsultantLogoUrl() || NirantaraLogo}
          alt="Vajra Logo"
          style={{ width: "60%", maxHeight: "60px", objectFit: "contain" }}
        />
      </Box>
      <div ref={sidebarRef} className={styles.sidebarList}>
        {renderSidebarItems()}
      </div>
    </Box>
  );
};

export default InitialQuestionareSidebar;
